import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Constants } from '../../../constants/constants';
import { ReviewService } from '../../../services/review.service';
import { CommonDataService } from '../../../services/common-data.service';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { SiFormFieldComponent } from '../../Si-Web-Toolkit/si-form-field/si-form-field.component';
import { SiDropDownComponent } from '../../Si-Web-Toolkit/si-drop-down/si-drop-down.component';
import { SiRadioButtonComponent } from "../../Si-Web-Toolkit/si-radio-button/si-radio-button.component";
import { SiCheckBoxGroupComponent } from "../../Si-Web-Toolkit/si-check-box-group/si-check-box-group.component";
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { IExhaustFansForm } from '../../../models/SolChecklistInterfaces/IExhaustFansForm';
import { IAirHandlersForm } from '../../../models/SolChecklistInterfaces/IAirHandlersForm';
import { IHumidifiersForm } from '../../../models/SolChecklistInterfaces/IHumidifiersForm';
import { IExecutionScheduleForm } from '../../../models/SolChecklistInterfaces/IExecutionScheduleForm';
import { ISecurityNetworkForm } from '../../../models/SolChecklistInterfaces/ISecurityNetworkForm';
import { ISecurityRetrofitForm } from '../../../models/SolChecklistInterfaces/ISecurityRetrofitForm';
import { IAccessControlSystemForm } from '../../../models/SolChecklistInterfaces/IAccessControlSystemForm';
import { ICctvSystemsForm } from '../../../models/SolChecklistInterfaces/ICctvSystemsForm';
import { IIntercomSystemForm } from '../../../models/SolChecklistInterfaces/IIntercomSystemForm';
import { IIntrusionSystemForm } from '../../../models/SolChecklistInterfaces/IIntrusionSystemForm';
import { SiCheckBoxComponent } from '../../Si-Web-Toolkit/si-check-box/si-check-box.component';
import { SiTextAreaComponent } from '../../Si-Web-Toolkit/si-text-area/si-text-area.component';
import { CostReviewSecurityEnUsExecutionSchedule } from '../../../models/CostReviewSecurityEnUsExecutionSchedule';
import { CostReviewSecurityEnUsIntercomSystem } from '../../../models/CostReviewSecurityEnUsIntercomSystem';
import { CostReviewSecurityEnUsAccessControlSystems } from '../../../models/CostReviewSecurityEnUsAccessControlSystems';
import { CostReviewSecurityEnUsCctvSystems } from '../../../models/CostReviewSecurityEnUsCctvSystems';
import { CostReviewSecurityEnUsIntrusionSystem } from '../../../models/CostReviewSecurityEnUsIntrusionSystem';
import { CostReviewSecurityEnUsNetwork } from '../../../models/CostReviewSecurityEnUsNetwork';
import { CostReviewSecurityEnUsRetrofitConsiderations } from '../../../models/CostReviewSecurityEnUsRetrofitConsiderations';
import { CostReviewAttributes } from '../../../models/CostReviewAttributes';
import { CostReviewHumidifiers } from '../../../models/CostReviewHumidifiers';
import { CostReviewExhaustFans } from '../../../models/CostReviewExhaustFans';
import { CostReviewAirHandlers } from '../../../models/CostReviewAirHandlers';
import { CostReviewSecurityEnUsAttributes } from '../../../models/CostReviewSecurityEnUsAttributes';
import { CostReviewEstimateType, ReviewDetailTabsService } from '../../../public-api';
import { SiComboBoxComponent } from '../../Si-Web-Toolkit/si-combo-box/si-combo-box.component';
type SecurityExecutionScheduleFormControls = Pick<
  IExecutionScheduleForm,
  | 'phasedInstallation'
  | 'describePhasingConsiderations'
  | 'projectSchedulePublished'
  | 'officeTechnicalCompetencies'
  | 'localOfficeStaffed'
  | 'specialTaggingRequirements'
  | 'specialCutoverRequirements'
>;

type ExhaustFansFormValue = ReturnType<FormGroup<IExhaustFansForm>['getRawValue']>;
type AirHandlersFormValue = ReturnType<FormGroup<IAirHandlersForm>['getRawValue']>;
type HumidifiersFormValue = ReturnType<FormGroup<IHumidifiersForm>['getRawValue']>;
type SecurityExecutionScheduleFormValue = ReturnType<FormGroup<SecurityExecutionScheduleFormControls>['getRawValue']>;
type SecurityNetworkFormValue = ReturnType<FormGroup<ISecurityNetworkForm>['getRawValue']>;
type SecurityRetrofitFormValue = ReturnType<FormGroup<ISecurityRetrofitForm>['getRawValue']>;
type AccessControlSystemFormValue = ReturnType<FormGroup<IAccessControlSystemForm>['getRawValue']>;
type CctvSystemsFormValue = ReturnType<FormGroup<ICctvSystemsForm>['getRawValue']>;
type IntercomSystemFormValue = ReturnType<FormGroup<IIntercomSystemForm>['getRawValue']>;
type IntrusionSystemFormValue = ReturnType<FormGroup<IIntrusionSystemForm>['getRawValue']>;

import { TrackChangesDirective } from '../../../Directives/track-changes.directive';
import { CommonFunctionsService } from '../../../services/common-functions.service';

@Component({
  selector: 'lib-sol-checklist-part-two',
  imports: [
    MatLabel,
    CommonModule,
    TranslatePipe,
    SiFormFieldComponent,
    SiDropDownComponent,
    SiRadioButtonComponent,
    SiCheckBoxGroupComponent,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    SiCheckBoxComponent,
    SiTextAreaComponent,
    TrackChangesDirective,
    SiComboBoxComponent
  ],
  templateUrl: './sol-checklist-part-two.component.html',
  styleUrl: './sol-checklist-part-two.component.scss'
})
export class SolChecklistPartTwoComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  vendorList: { label: string; value: string }[] = [];
  cctvVendorList: { label: string; value: string }[] = [];
  intrusionSystemList: { label: string; value: string }[] = [];

  constants = Constants;
  business = '';
  isSalePriceGreaterThan150 = false;
  isReadMode = false;
  isDisabled = false;
  sectionToggleState: Record<string, boolean> = {};
  sectionDisableState: Record<string, boolean> = {};
  estimateType = CostReviewEstimateType.None;
  optionalWorkflowCondition = false;

  // Checkbox group options arrays
  networkOptionsArray = ['NETWORK_OPTION_FIBER', 'NETWORK_OPTION_COPPER', 'NETWORK_OPTION_STYLE_4', 'NETWORK_OPTION_STYLE_7'];
  cctvIntegrationRequiredOptions = ['ACCESS_CONTROL', 'MNS', 'INTERCOM_MNS', 'DESIGO_CC', 'FACIAL_RECOGNITION'];
  doorHardwareTypeOptions = ['STRIKE', 'MAG_LOCK', 'SALTO', 'WIRELESS_APERIO'];

  isSectionChecked(section: string): boolean {
    return !!this.sectionToggleState[section];
  }

  private getSectionFormGroup(section: string): FormGroup | undefined {
    switch (section) {
      case 'accessControlSystemFormGroup': return this.accessControlSystemFormGroup;
      case 'cctvSystemsFormGroup': return this.cctvSystemsFormGroup;
      case 'intercomSystemFormGroup': return this.intercomSystemFormGroup;
      case 'intrusionSystemFormGroup': return this.intrusionSystemFormGroup;
      default: return undefined;
    }
  }

  isVendorEnabled(section: string, checkedControlName: string): boolean {
    return this.isSectionChecked(section) &&
      !!this.getSectionFormGroup(section)?.get(checkedControlName)?.value;
  }

  private isSectionApplicable(section: string): boolean {
    switch (section) {
      case 'exhaustFansFormGroup':
        return !!this.costReviewExhaustFans?.isExhaustFansApplicable;
      case 'airHandlersFormGroup':
        return !!this.costReviewAirHandler?.isAirHandlersApplicable;
      case 'humidifiersFormGroup':
        return !!this.costReviewHumidiFires?.isHumidifiersApplicable;
      case 'executionScheduleFormGroup':
        return !!this.costReviewSecurityEnUsExecutionSchedule?.isExecutionScheduleChecked;
      case 'securityNetworkFormGroup':
        return !!this.costReviewSecurityEnUsNetwork?.isNetworkChecked;
      case 'securityRetrofitFormGroup':
        return !!this.costReviewSecurityEnUsRetrofitConsiderations?.isRetrofitConsiderationsChecked;
      case 'accessControlSystemFormGroup':
        return !!this.costReviewSecurityEnUsAccessControlSystems?.isAccessSystemChecked;
      case 'cctvSystemsFormGroup':
        return !!this.costReviewSecurityEnUsCctvSystems?.isCCTVSystemsChecked;
      case 'intercomSystemFormGroup':
        return !!this.costReviewSecurityEnUsIntercomSystem?.isIntercomSystemChecked;
      case 'intrusionSystemFormGroup':
        return !!this.costReviewSecurityEnUsIntrusionSystem?.isIntrusionSystemChecked;
      default:
        return true;
    }
  }

  private getSectionDisabledState(section: string): boolean {
    return this.isDisabled || !this.isSectionChecked(section);
  }

  private initializeSectionToggleState(): void {
    const sections = [
      'exhaustFansFormGroup',
      'airHandlersFormGroup',
      'humidifiersFormGroup',
      'executionScheduleFormGroup',
      'securityNetworkFormGroup',
      'securityRetrofitFormGroup',
      'accessControlSystemFormGroup',
      'cctvSystemsFormGroup',
      'intercomSystemFormGroup',
      'intrusionSystemFormGroup'
    ];

    for (const section of sections) {
      this.sectionToggleState[section] = this.isSectionApplicable(section);
      this.sectionDisableState[section] = this.getSectionDisabledState(section);
    }
  }

  onSectionToggle(section: string, checked: boolean): void {
    this.sectionToggleState[section] = checked;
    this.sectionDisableState[section] = this.getSectionDisabledState(section);
    this.persistSectionToggleState(section, checked);
  }

  private persistSectionToggleState(section: string, checked: boolean): void {
    switch (section) {
      case 'exhaustFansFormGroup':
        this.costReviewExhaustFans.isExhaustFansApplicable = checked;
        break;
      case 'airHandlersFormGroup':
        this.costReviewAirHandler.isAirHandlersApplicable = checked;
        break;
      case 'humidifiersFormGroup':
        this.costReviewHumidiFires.isHumidifiersApplicable = checked;
        break;
      case 'executionScheduleFormGroup':
        this.costReviewSecurityEnUsExecutionSchedule.isExecutionScheduleChecked = checked;
        break;
      case 'securityNetworkFormGroup':
        this.costReviewSecurityEnUsNetwork.isNetworkChecked = checked;
        break;
      case 'securityRetrofitFormGroup':
        this.costReviewSecurityEnUsRetrofitConsiderations.isRetrofitConsiderationsChecked = checked;
        break;
      case 'accessControlSystemFormGroup':
        this.costReviewSecurityEnUsAccessControlSystems.isAccessSystemChecked = checked;
        break;
      case 'cctvSystemsFormGroup':
        this.costReviewSecurityEnUsCctvSystems.isCCTVSystemsChecked = checked;
        break;
      case 'intercomSystemFormGroup':
        this.costReviewSecurityEnUsIntercomSystem.isIntercomSystemChecked = checked;
        break;
      case 'intrusionSystemFormGroup':
        this.costReviewSecurityEnUsIntrusionSystem.isIntrusionSystemChecked = checked;
        break;
    }
  }

  isSectionFieldsetDisabled(section: string): boolean {
    const disabled = this.getSectionDisabledState(section);
    this.sectionDisableState[section] = disabled;
    return disabled;
  }

  get isSecurity(): boolean {
    return this.business === this.constants.BUSINESS_SECURITY;
  }

  get isFire(): boolean {
    return this.business === this.constants.BUSINESS_FIRE;
  }

  get isFireOrSecurity(): boolean {
    return this.isFire || this.isSecurity;
  }

  get isAem(): boolean {
    return (this.business === this.constants.BUSINESS_AUTOMATION) ||
           (this.business === this.constants.BUSINESS_ELECTRICAL) ||
           (this.business === this.constants.BUSINESS_MECHANICAL);
  }

  get isAemWithHighSalePrice(): boolean {
    return this.isAem && this.isSalePriceGreaterThan150;
  }

  get isExecutionScheduleLocked(): boolean {
    return this.optionalWorkflowCondition && this.costReviewSecurityEnUsExecutionSchedule?.isExecutionScheduleChecked;
  }

  //  data objects actually used by forms
  costReviewSecurityEnUsExecutionSchedule: CostReviewSecurityEnUsExecutionSchedule = new CostReviewSecurityEnUsExecutionSchedule();
  costReviewSecurityEnUsIntercomSystem = new CostReviewSecurityEnUsIntercomSystem();
  costReviewSecurityEnUsCctvSystems = new CostReviewSecurityEnUsCctvSystems();
  costReviewSecurityEnUsAccessControlSystems = new CostReviewSecurityEnUsAccessControlSystems();
  costReviewSecurityEnUsNetwork = new CostReviewSecurityEnUsNetwork();
  costReviewSecurityEnUsRetrofitConsiderations = new CostReviewSecurityEnUsRetrofitConsiderations();
  costReviewSecurityEnUsIntrusionSystem = new CostReviewSecurityEnUsIntrusionSystem();
  costReviewExhaustFans = new CostReviewExhaustFans();
  costReviewAirHandler = new CostReviewAirHandlers();
  costReviewHumidiFires = new CostReviewHumidifiers();

  // FormGroups
  exhaustFansFormGroup!: FormGroup<IExhaustFansForm>;
  airHandlersFormGroup!: FormGroup<IAirHandlersForm>;
  humidifiersFormGroup!: FormGroup<IHumidifiersForm>;
  executionScheduleFormGroup!: FormGroup<SecurityExecutionScheduleFormControls>;
  securityNetworkFormGroup!: FormGroup<ISecurityNetworkForm>;
  securityRetrofitFormGroup!: FormGroup<ISecurityRetrofitForm>;
  accessControlSystemFormGroup!: FormGroup<IAccessControlSystemForm>;
  cctvSystemsFormGroup!: FormGroup<ICctvSystemsForm>;
  intercomSystemFormGroup!: FormGroup<IIntercomSystemForm>;
  intrusionSystemFormGroup!: FormGroup<IIntrusionSystemForm>;

  constructor(
    private readonly reviewService: ReviewService,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly commonDataService: CommonDataService,
    private readonly reviewDetailsTabService: ReviewDetailTabsService,
    private readonly commonFunctionsService: CommonFunctionsService
  ) { }

  ngOnInit() {
    this.getReviewLibraryState();
    this.estimateType = this.convertToEstimateType(
      this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType().optionalEstimateType
    );
    this.isSalePriceGreaterThan150 = this.estimateType === CostReviewEstimateType.EstimateGreater150K;
    this.vendorList = this.getDropdownValues(this.constants.ACCESS_CONTROL_SYSTEM_VENDOR);
    this.cctvVendorList = this.getDropdownValues(this.constants.CCTV_SYSTEM_VENDOR);
    this.intrusionSystemList = this.getDropdownValues(this.constants.INTRUSION_SYSTEM_VENDOR);
    this.optionalWorkflowCondition = this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType()?.optionalWorkflow || false;
    this.safeInitializeAndMap();
    this.createAllFormGroups();
    this.initializeSectionToggleState();
    this.setupReadOnlyMode();
    this.subscribeToReviewFormDataSyncRequest();
  }

  /** Allows the host app to force this tab to push its latest data without waiting for tab switch/destroy. */
  private subscribeToReviewFormDataSyncRequest(): void {
    this.reviewDetailsTabService.getReviewFormDataSyncRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pushFormDataToService());
  }
   private convertToEstimateType(optionalEstimateType: CostReviewEstimateType | undefined): CostReviewEstimateType {
    switch (optionalEstimateType) {
      case CostReviewEstimateType.EstimateLess50K:
        return CostReviewEstimateType.EstimateLess50K;
      case CostReviewEstimateType.Estimate50_150K:
        return CostReviewEstimateType.Estimate50_150K;      
      case CostReviewEstimateType.EstimateGreater150K:
        return CostReviewEstimateType.EstimateGreater150K;
      default:
        return CostReviewEstimateType.None;
    }
  }

  ngOnDestroy(): void {
    this.pushFormDataToService();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private pushFormDataToService(): void {
    switch (this.business) {
      case this.constants.BUSINESS_AUTOMATION:
      case this.constants.BUSINESS_ELECTRICAL:
      case this.constants.BUSINESS_MECHANICAL:
        this.pushExhaustFansFormData();
        this.pushAirHandlersFormData();
        this.pushHumidifiersFormData();
        break;

      case this.constants.BUSINESS_SECURITY:
        this.pushSecurityNetworkFormData();
        this.pushSecurityRetrofitFormData();
        this.pushAccessControlSystemFormData();
        this.pushCctvSystemsFormData();
        this.pushIntercomSystemFormData();
        this.pushIntrusionSystemFormData();
        this.pushExecutionScheduleSecurityFormData();
        break;
    }

    this.attachReviewObjectReferences();
  }

  private attachReviewObjectReferences(): void {
    const reviewObject = this.reviewService.getOrCreateReviewObjectForBusiness(this.business);
    if (!reviewObject) {
      return;
    }

    switch (this.business) {
      case this.constants.BUSINESS_AUTOMATION:
      case this.constants.BUSINESS_ELECTRICAL:
      case this.constants.BUSINESS_MECHANICAL: {
        const attrs = reviewObject as CostReviewAttributes;
        attrs.costReviewExhaustFans = this.costReviewExhaustFans;
        attrs.costReviewAirHandlers = this.costReviewAirHandler;
        attrs.costReviewHumidifiers = this.costReviewHumidiFires;
        break;
      }

      case this.constants.BUSINESS_SECURITY: {
        const sec = reviewObject as CostReviewSecurityEnUsAttributes;
        sec.costReviewSecurityEnUsExecutionSchedule = this.costReviewSecurityEnUsExecutionSchedule;
        sec.costReviewSecurityEnUsIntercomSystem = this.costReviewSecurityEnUsIntercomSystem;
        sec.costReviewSecurityEnUsCctvSystems = this.costReviewSecurityEnUsCctvSystems;
        sec.costReviewSecurityEnUsAccessControlSystems = this.costReviewSecurityEnUsAccessControlSystems;
        sec.costReviewSecurityEnUsNetwork = this.costReviewSecurityEnUsNetwork;
        sec.costReviewSecurityEnUsRetrofitConsiderations = this.costReviewSecurityEnUsRetrofitConsiderations;
        sec.costReviewSecurityEnUsIntrusionSystem = this.costReviewSecurityEnUsIntrusionSystem;
        break;
      }
    }
  }

  private pushExhaustFansFormData(): void {
    if (!this.exhaustFansFormGroup) {
      return;
    }
    if (!this.costReviewExhaustFans) {
      this.costReviewExhaustFans = new CostReviewExhaustFans();
    }

    const formData: ExhaustFansFormValue = this.exhaustFansFormGroup.getRawValue();
    this.costReviewExhaustFans.numberOfExhaustFans = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfExhaustFans, this.costReviewExhaustFans.numberOfExhaustFans);
    this.costReviewExhaustFans.manufacturer = formData.manufacturer;
    this.costReviewExhaustFans.unitsControlsFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsControlsFurnishedBy, this.costReviewExhaustFans.unitsControlsFurnishedBy);
    this.costReviewExhaustFans.unitsControlsInstalledBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsControlsInstalledBy, this.costReviewExhaustFans.unitsControlsInstalledBy);
    this.costReviewExhaustFans.unitsControlsWiredBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsControlsWiredBy, this.costReviewExhaustFans.unitsControlsWiredBy);
  }

  private pushAirHandlersFormData(): void {
    if (!this.airHandlersFormGroup) {
      return;
    }
    if (!this.costReviewAirHandler) {
      this.costReviewAirHandler = new CostReviewAirHandlers();
    }

    const formData: AirHandlersFormValue = this.airHandlersFormGroup.getRawValue();
    this.costReviewAirHandler.numberOfAirHandlerUnits = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfAirHandlerUnits, this.costReviewAirHandler.numberOfAirHandlerUnits);
    this.costReviewAirHandler.manufacturer = formData.manufacturer;
    this.costReviewAirHandler.unitsControlsFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsControlsFurnishedBy, this.costReviewAirHandler.unitsControlsFurnishedBy);
    this.costReviewAirHandler.unitsControlsInstalledBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsControlsInstalledBy, this.costReviewAirHandler.unitsControlsInstalledBy);
    this.costReviewAirHandler.unitsControlsWiredBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsControlsWiredBy, this.costReviewAirHandler.unitsControlsWiredBy);
    this.costReviewAirHandler.specialSensorsRequired = this.getParameterCode(this.constants.YES_NO, formData.specialSensorsRequired, this.costReviewAirHandler.specialSensorsRequired);
    this.costReviewAirHandler.numberOfFans = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfFans, this.costReviewAirHandler.numberOfFans);
    this.costReviewAirHandler.description = formData.description;
  }

  private pushHumidifiersFormData(): void {
    if (!this.humidifiersFormGroup) {
      return;
    }
    if (!this.costReviewHumidiFires) {
      this.costReviewHumidiFires = new CostReviewHumidifiers();
    }

    const formData: HumidifiersFormValue = this.humidifiersFormGroup.getRawValue();
    this.costReviewHumidiFires.numberOfHumidifires = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfHumidifires, this.costReviewHumidiFires.numberOfHumidifires);
    this.costReviewHumidiFires.manufacturer = formData.manufacturer;
    this.costReviewHumidiFires.unitsControlsFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsControlsFurnishedBy, this.costReviewHumidiFires.unitsControlsFurnishedBy);
    this.costReviewHumidiFires.unitsControlsInstalledBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsControlsInstalledBy, this.costReviewHumidiFires.unitsControlsInstalledBy);
    this.costReviewHumidiFires.unitsControlsWiredBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsControlsWiredBy, this.costReviewHumidiFires.unitsControlsWiredBy);
  }

  private pushExecutionScheduleSecurityFormData(): void {
    if (!this.executionScheduleFormGroup) {
      return;
    }
    if (!this.costReviewSecurityEnUsExecutionSchedule) {
      this.costReviewSecurityEnUsExecutionSchedule = new CostReviewSecurityEnUsExecutionSchedule();
    }

    const formData: SecurityExecutionScheduleFormValue = this.executionScheduleFormGroup.getRawValue();
    this.costReviewSecurityEnUsExecutionSchedule.phasedInstallation = this.getParameterCode(this.constants.YES_NO, formData.phasedInstallation, this.costReviewSecurityEnUsExecutionSchedule.phasedInstallation);
    this.costReviewSecurityEnUsExecutionSchedule.describePhasingConsiderations = formData.describePhasingConsiderations;
    this.costReviewSecurityEnUsExecutionSchedule.projectSchedulePublished = this.getParameterCode(this.constants.YES_NO, formData.projectSchedulePublished, this.costReviewSecurityEnUsExecutionSchedule.projectSchedulePublished);
    this.costReviewSecurityEnUsExecutionSchedule.officeTechnicalCompetencies = this.getParameterCode(this.constants.YES_NO, formData.officeTechnicalCompetencies, this.costReviewSecurityEnUsExecutionSchedule.officeTechnicalCompetencies);
    this.costReviewSecurityEnUsExecutionSchedule.localOfficeStaffed = this.getParameterCode(this.constants.YES_NO, formData.localOfficeStaffed, this.costReviewSecurityEnUsExecutionSchedule.localOfficeStaffed);
    this.costReviewSecurityEnUsExecutionSchedule.specialTaggingRequirements = this.getParameterCode(this.constants.YES_NO, formData.specialTaggingRequirements, this.costReviewSecurityEnUsExecutionSchedule.specialTaggingRequirements);
    this.costReviewSecurityEnUsExecutionSchedule.specialCutoverRequirements = this.getParameterCode(this.constants.YES_NO, formData.specialCutoverRequirements, this.costReviewSecurityEnUsExecutionSchedule.specialCutoverRequirements);
  }

  private pushSecurityNetworkFormData(): void {
    if (!this.securityNetworkFormGroup) {
      return;
    }
    if (!this.costReviewSecurityEnUsNetwork) {
      this.costReviewSecurityEnUsNetwork = new CostReviewSecurityEnUsNetwork();
    }

    const formData: SecurityNetworkFormValue = this.securityNetworkFormGroup.getRawValue();
    this.costReviewSecurityEnUsNetwork.networkStyle = this.getParameterCode(this.constants.NETWORK_STYLE, formData.networkStyle, this.costReviewSecurityEnUsNetwork.networkStyle);
    this.costReviewSecurityEnUsNetwork.networkOptions = this.commonFunctionsService.toNumberArray(formData.networkOptions, this.networkOptionsArray);
  }

  private pushSecurityRetrofitFormData(): void {
    if (!this.securityRetrofitFormGroup) {
      return;
    }
    if (!this.costReviewSecurityEnUsRetrofitConsiderations) {
      this.costReviewSecurityEnUsRetrofitConsiderations = new CostReviewSecurityEnUsRetrofitConsiderations();
    }

    const formData: SecurityRetrofitFormValue = this.securityRetrofitFormGroup.getRawValue();
    this.costReviewSecurityEnUsRetrofitConsiderations.reuseSystemComponents = this.getParameterCode(this.constants.YES_NO, formData.reuseSystemComponents, this.costReviewSecurityEnUsRetrofitConsiderations.reuseSystemComponents);
    this.costReviewSecurityEnUsRetrofitConsiderations.existingWiringReused = this.getParameterCode(this.constants.YES_NO, formData.existingWiringReused, this.costReviewSecurityEnUsRetrofitConsiderations.existingWiringReused);
  }

  private pushAccessControlSystemFormData(): void {
    if (!this.accessControlSystemFormGroup) {
      return;
    }
    if (!this.costReviewSecurityEnUsAccessControlSystems) {
      this.costReviewSecurityEnUsAccessControlSystems = new CostReviewSecurityEnUsAccessControlSystems();
    }

    const formData: AccessControlSystemFormValue = this.accessControlSystemFormGroup.getRawValue();
    this.costReviewSecurityEnUsAccessControlSystems.integrationWithOtherSystems = this.getParameterCode(this.constants.YES_NO, formData.integrationWithOtherSystems, this.costReviewSecurityEnUsAccessControlSystems.integrationWithOtherSystems);
    this.costReviewSecurityEnUsAccessControlSystems.isNewVendorChecked = formData.isNewVendorChecked;
    this.costReviewSecurityEnUsAccessControlSystems.newVendor = formData.newVendor;
    this.costReviewSecurityEnUsAccessControlSystems.isReplacementVendorChecked = formData.isReplacementVendorChecked;
    this.costReviewSecurityEnUsAccessControlSystems.replacementVendor = formData.replacementVendor;
    this.costReviewSecurityEnUsAccessControlSystems.isExpansionVendorChecked = formData.isExpansionVendorChecked;
    this.costReviewSecurityEnUsAccessControlSystems.expansionVendor = formData.expansionVendor;
    this.costReviewSecurityEnUsAccessControlSystems.detailedDrawings = this.getParameterCode(this.constants.YES_NO, formData.detailedDrawings, this.costReviewSecurityEnUsAccessControlSystems.detailedDrawings);
    this.costReviewSecurityEnUsAccessControlSystems.firmElectricalQuote = this.getParameterCode(this.constants.YES_NO, formData.firmElectricalQuote, this.costReviewSecurityEnUsAccessControlSystems.firmElectricalQuote);
    this.costReviewSecurityEnUsAccessControlSystems.networkProvidedBy = this.getParameterCode(this.constants.SIEMENS_CUSTOMER, formData.networkProvidedBy, this.costReviewSecurityEnUsAccessControlSystems.networkProvidedBy);
    this.costReviewSecurityEnUsAccessControlSystems.serverProvidedBy = this.getParameterCode(this.constants.SIEMENS_CUSTOMER, formData.serverProvidedBy, this.costReviewSecurityEnUsAccessControlSystems.serverProvidedBy);
    this.costReviewSecurityEnUsAccessControlSystems.clientComputer = this.getParameterCode(this.constants.SIEMENS_CUSTOMER, formData.clientComputer, this.costReviewSecurityEnUsAccessControlSystems.clientComputer);
    this.costReviewSecurityEnUsAccessControlSystems.numberOfClients = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfClients, this.costReviewSecurityEnUsAccessControlSystems.numberOfClients);
    this.costReviewSecurityEnUsAccessControlSystems.risk = formData.risk;
    this.costReviewSecurityEnUsAccessControlSystems.numberOfPanels = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfPanels, this.costReviewSecurityEnUsAccessControlSystems.numberOfPanels);
    this.costReviewSecurityEnUsAccessControlSystems.numberOfReaders = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfReaders, this.costReviewSecurityEnUsAccessControlSystems.numberOfReaders);
    this.costReviewSecurityEnUsAccessControlSystems.numberOfContacts = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfContacts, this.costReviewSecurityEnUsAccessControlSystems.numberOfContacts);
    this.costReviewSecurityEnUsAccessControlSystems.numberOfREX = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfREX, this.costReviewSecurityEnUsAccessControlSystems.numberOfREX);
    this.costReviewSecurityEnUsAccessControlSystems.doorHardwareType = this.commonFunctionsService.toNumberArray(formData.doorHardwareType, this.doorHardwareTypeOptions);
    this.costReviewSecurityEnUsAccessControlSystems.doorHardwareProvidedBy = this.getParameterCode(this.constants.SIEMENS_OTHER_SUBCONTRACTOR, formData.doorHardwareProvidedBy, this.costReviewSecurityEnUsAccessControlSystems.doorHardwareProvidedBy);
    this.costReviewSecurityEnUsAccessControlSystems.doorHardwareInstalledBy = this.getParameterCode(this.constants.SIEMENS_OTHER_SUBCONTRACTOR, formData.doorHardwareInstalledBy, this.costReviewSecurityEnUsAccessControlSystems.doorHardwareInstalledBy);
    this.costReviewSecurityEnUsAccessControlSystems.numberOfDoors = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfDoors, this.costReviewSecurityEnUsAccessControlSystems.numberOfDoors);
    this.costReviewSecurityEnUsAccessControlSystems.localDoorAlarms = formData.localDoorAlarms;
    this.costReviewSecurityEnUsAccessControlSystems.employeesToTrain = this.commonFunctionsService.toNumberOrCurrent(formData.employeesToTrain, this.costReviewSecurityEnUsAccessControlSystems.employeesToTrain);
    this.costReviewSecurityEnUsAccessControlSystems.lowContractor = formData.lowContractor;
    this.costReviewSecurityEnUsAccessControlSystems.numberOfBidders = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfBidders, this.costReviewSecurityEnUsAccessControlSystems.numberOfBidders);
    this.costReviewSecurityEnUsAccessControlSystems.wireSpend = this.getParameterCode(this.constants.YES_NO, formData.wireSpend, this.costReviewSecurityEnUsAccessControlSystems.wireSpend);
    this.costReviewSecurityEnUsAccessControlSystems.thirdPartyCommodity = this.getParameterCode(this.constants.YES_NO, formData.thirdPartyCommodity, this.costReviewSecurityEnUsAccessControlSystems.thirdPartyCommodity);
  }

  private pushCctvSystemsFormData(): void {
    if (!this.cctvSystemsFormGroup) {
      return;
    }
    if (!this.costReviewSecurityEnUsCctvSystems) {
      this.costReviewSecurityEnUsCctvSystems = new CostReviewSecurityEnUsCctvSystems();
    }

    const formData: CctvSystemsFormValue = this.cctvSystemsFormGroup.getRawValue();
    this.costReviewSecurityEnUsCctvSystems.integrationWithOtherSystems = this.getParameterCode(this.constants.YES_NO, formData.integrationWithOtherSystems, this.costReviewSecurityEnUsCctvSystems.integrationWithOtherSystems);
    this.costReviewSecurityEnUsCctvSystems.isNewVendorChecked = formData.isNewVendorChecked;
    this.costReviewSecurityEnUsCctvSystems.newVendor = formData.newVendor;
    this.costReviewSecurityEnUsCctvSystems.isReplacementVendorChecked = formData.isReplacementVendorChecked;
    this.costReviewSecurityEnUsCctvSystems.replacementVendor = formData.replacementVendor;
    this.costReviewSecurityEnUsCctvSystems.isExpansionVendorChecked = formData.isExpansionVendorChecked;
    this.costReviewSecurityEnUsCctvSystems.expansionVendor = formData.expansionVendor;
    this.costReviewSecurityEnUsCctvSystems.serverProvidedBy = this.getParameterCode(this.constants.SIEMENS_CUSTOMER, formData.serverProvidedBy, this.costReviewSecurityEnUsCctvSystems.serverProvidedBy);
    this.costReviewSecurityEnUsCctvSystems.storageProvidedBy = this.getParameterCode(this.constants.SIEMENS_CUSTOMER, formData.storageProvidedBy, this.costReviewSecurityEnUsCctvSystems.storageProvidedBy);
    this.costReviewSecurityEnUsCctvSystems.clientComputer = this.getParameterCode(this.constants.SIEMENS_CUSTOMER, formData.clientComputer, this.costReviewSecurityEnUsCctvSystems.clientComputer);
    this.costReviewSecurityEnUsCctvSystems.numberOfClients = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfClients, this.costReviewSecurityEnUsCctvSystems.numberOfClients);
    this.costReviewSecurityEnUsCctvSystems.integrationRequired = this.commonFunctionsService.toNumberArray(formData.integrationRequired, this.cctvIntegrationRequiredOptions);
    this.costReviewSecurityEnUsCctvSystems.liftRequired = this.getParameterCode(this.constants.YES_NO, formData.liftRequired, this.costReviewSecurityEnUsCctvSystems.liftRequired);
    this.costReviewSecurityEnUsCctvSystems.risk = formData.risk;
    this.costReviewSecurityEnUsCctvSystems.numberOfControllers = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfControllers, this.costReviewSecurityEnUsCctvSystems.numberOfControllers);
    this.costReviewSecurityEnUsCctvSystems.numberOfInsideCameras = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfInsideCameras, this.costReviewSecurityEnUsCctvSystems.numberOfInsideCameras);
    this.costReviewSecurityEnUsCctvSystems.numberOfOutsideCameras = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfOutsideCameras, this.costReviewSecurityEnUsCctvSystems.numberOfOutsideCameras);
    this.costReviewSecurityEnUsCctvSystems.panAndTiltCameras = formData.panAndTiltCameras;
    this.costReviewSecurityEnUsCctvSystems.expectedStorageTime = this.commonFunctionsService.toNumberOrCurrent(formData.expectedStorageTime, this.costReviewSecurityEnUsCctvSystems.expectedStorageTime);
    this.costReviewSecurityEnUsCctvSystems.archivingMedia = this.getParameterCode(this.constants.YES_NO, formData.archivingMedia, this.costReviewSecurityEnUsCctvSystems.archivingMedia);
    this.costReviewSecurityEnUsCctvSystems.employeesToTrain = this.commonFunctionsService.toNumberOrCurrent(formData.employeesToTrain, this.costReviewSecurityEnUsCctvSystems.employeesToTrain);
    this.costReviewSecurityEnUsCctvSystems.lowContractor = formData.lowContractor;
    this.costReviewSecurityEnUsCctvSystems.numberOfBidders = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfBidders, this.costReviewSecurityEnUsCctvSystems.numberOfBidders);
  }

  private pushIntercomSystemFormData(): void {
    if (!this.intercomSystemFormGroup) {
      return;
    }
    if (!this.costReviewSecurityEnUsIntercomSystem) {
      this.costReviewSecurityEnUsIntercomSystem = new CostReviewSecurityEnUsIntercomSystem();
    }

    const formData: IntercomSystemFormValue = this.intercomSystemFormGroup.getRawValue();
    this.costReviewSecurityEnUsIntercomSystem.integrationWithOtherSystems = this.getParameterCode(this.constants.YES_NO, formData.integrationWithOtherSystems, this.costReviewSecurityEnUsIntercomSystem.integrationWithOtherSystems);
    this.costReviewSecurityEnUsIntercomSystem.isNewVendorChecked = formData.isNewVendorChecked;
    this.costReviewSecurityEnUsIntercomSystem.newVendor = formData.newVendor;
    this.costReviewSecurityEnUsIntercomSystem.isReplacementVendorChecked = formData.isReplacementVendorChecked;
    this.costReviewSecurityEnUsIntercomSystem.replacementVendor = formData.replacementVendor;
    this.costReviewSecurityEnUsIntercomSystem.isExpansionVendorChecked = formData.isExpansionVendorChecked;
    this.costReviewSecurityEnUsIntercomSystem.expansionVendor = formData.expansionVendor;
    this.costReviewSecurityEnUsIntercomSystem.risk = formData.risk;
    this.costReviewSecurityEnUsIntercomSystem.numberOfMasters = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfMasters, this.costReviewSecurityEnUsIntercomSystem.numberOfMasters);
    this.costReviewSecurityEnUsIntercomSystem.numberOfSubstations = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfSubstations, this.costReviewSecurityEnUsIntercomSystem.numberOfSubstations);
    this.costReviewSecurityEnUsIntercomSystem.employeesToTrain = this.commonFunctionsService.toNumberOrCurrent(formData.employeesToTrain, this.costReviewSecurityEnUsIntercomSystem.employeesToTrain);
    this.costReviewSecurityEnUsIntercomSystem.lowContractor = formData.lowContractor;
    this.costReviewSecurityEnUsIntercomSystem.numberOfBidders = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfBidders, this.costReviewSecurityEnUsIntercomSystem.numberOfBidders);
  }

  private pushIntrusionSystemFormData(): void {
    if (!this.intrusionSystemFormGroup) {
      return;
    }
    if (!this.costReviewSecurityEnUsIntrusionSystem) {
      this.costReviewSecurityEnUsIntrusionSystem = new CostReviewSecurityEnUsIntrusionSystem();
    }

    const formData: IntrusionSystemFormValue = this.intrusionSystemFormGroup.getRawValue();
    this.costReviewSecurityEnUsIntrusionSystem.integrationWithOtherSystems = this.getParameterCode(this.constants.YES_NO, formData.integrationWithOtherSystems, this.costReviewSecurityEnUsIntrusionSystem.integrationWithOtherSystems);
    this.costReviewSecurityEnUsIntrusionSystem.isNewVendorChecked = formData.isNewVendorChecked;
    this.costReviewSecurityEnUsIntrusionSystem.newVendor = formData.newVendor;
    this.costReviewSecurityEnUsIntrusionSystem.isReplacementVendorChecked = formData.isReplacementVendorChecked;
    this.costReviewSecurityEnUsIntrusionSystem.replacementVendor = formData.replacementVendor;
    this.costReviewSecurityEnUsIntrusionSystem.isExpansionVendorChecked = formData.isExpansionVendorChecked;
    this.costReviewSecurityEnUsIntrusionSystem.expansionVendor = formData.expansionVendor;
    this.costReviewSecurityEnUsIntrusionSystem.isMonitoringVendorChecked = formData.isMonitoringVendorChecked;
    this.costReviewSecurityEnUsIntrusionSystem.monitoringVendor = formData.monitoringVendor;
    this.costReviewSecurityEnUsIntrusionSystem.detailedDrawings = this.getParameterCode(this.constants.YES_NO, formData.detailedDrawings, this.costReviewSecurityEnUsIntrusionSystem.detailedDrawings);
    this.costReviewSecurityEnUsIntrusionSystem.liftRequired = this.getParameterCode(this.constants.YES_NO, formData.liftRequired, this.costReviewSecurityEnUsIntrusionSystem.liftRequired);
    this.costReviewSecurityEnUsIntrusionSystem.risk = formData.risk;
    this.costReviewSecurityEnUsIntrusionSystem.numberOfPanels = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfPanels, this.costReviewSecurityEnUsIntrusionSystem.numberOfPanels);
    this.costReviewSecurityEnUsIntrusionSystem.numberOfKeypads = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfKeypads, this.costReviewSecurityEnUsIntrusionSystem.numberOfKeypads);
    this.costReviewSecurityEnUsIntrusionSystem.numberOfContacts = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfContacts, this.costReviewSecurityEnUsIntrusionSystem.numberOfContacts);
    this.costReviewSecurityEnUsIntrusionSystem.numberOfMotionDetectors = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfMotionDetectors, this.costReviewSecurityEnUsIntrusionSystem.numberOfMotionDetectors);
    this.costReviewSecurityEnUsIntrusionSystem.localAudibleDevices = this.commonFunctionsService.toNumberOrCurrent(formData.localAudibleDevices, this.costReviewSecurityEnUsIntrusionSystem.localAudibleDevices);
    this.costReviewSecurityEnUsIntrusionSystem.employeesToTrain = this.commonFunctionsService.toNumberOrCurrent(formData.employeesToTrain, this.costReviewSecurityEnUsIntrusionSystem.employeesToTrain);
    this.costReviewSecurityEnUsIntrusionSystem.numberOfBidders = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfBidders, this.costReviewSecurityEnUsIntrusionSystem.numberOfBidders);
    this.costReviewSecurityEnUsIntrusionSystem.lowContractor = formData.lowContractor;
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.libraryReadOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly: boolean) => {
        const isLibraryUsedInSQ = this.reviewDetailsTabService.getIsLibraryUsedInSQ();
        if (isLibraryUsedInSQ) {
          this.isDisabled = readOnly;
          this.isReadMode = false;
          this.enableAllFormGroups();
        } else {
          this.isReadMode = readOnly;
           this.disableAllFormGroups();
        }
      });
  }

  private disableAllFormGroups(): void {
    this.exhaustFansFormGroup?.disable();
    this.airHandlersFormGroup?.disable();
    this.humidifiersFormGroup?.disable();
    this.executionScheduleFormGroup?.disable();
    this.securityNetworkFormGroup?.disable();
    this.securityRetrofitFormGroup?.disable();
    this.accessControlSystemFormGroup?.disable();
    this.cctvSystemsFormGroup?.disable();
    this.intercomSystemFormGroup?.disable();
    this.intrusionSystemFormGroup?.disable();
  }

  private enableAllFormGroups(): void {
    this.exhaustFansFormGroup?.enable();
    this.airHandlersFormGroup?.enable();
    this.humidifiersFormGroup?.enable();
    this.executionScheduleFormGroup?.enable();
    this.securityNetworkFormGroup?.enable();
    this.securityRetrofitFormGroup?.enable();
    this.accessControlSystemFormGroup?.enable();
    this.cctvSystemsFormGroup?.enable();
    this.intercomSystemFormGroup?.enable();
    this.intrusionSystemFormGroup?.enable();
  }

  private safeInitializeAndMap() {
    const costReviewData = this.reviewService.costReviewDetails.reviewObject;
    this.business = this.reviewService.getBusiness();

    if (!costReviewData) {
      return;
    }

    switch(this.business) {
      case this.constants.BUSINESS_AUTOMATION:
      case this.constants.BUSINESS_ELECTRICAL:
      case this.constants.BUSINESS_MECHANICAL: {
        const costReviewAutomationData = costReviewData as CostReviewAttributes;
        this.costReviewExhaustFans = costReviewAutomationData.costReviewExhaustFans ?? this.costReviewExhaustFans;
        this.costReviewAirHandler = costReviewAutomationData.costReviewAirHandlers ?? this.costReviewAirHandler;
        this.costReviewHumidiFires = costReviewAutomationData.costReviewHumidifiers ?? this.costReviewHumidiFires;
        break;
      }
      case this.constants.BUSINESS_SECURITY: {
        const costReviewSecurityData = costReviewData as CostReviewSecurityEnUsAttributes;
        this.costReviewSecurityEnUsExecutionSchedule = costReviewSecurityData.costReviewSecurityEnUsExecutionSchedule ?? this.costReviewSecurityEnUsExecutionSchedule;
        this.costReviewSecurityEnUsIntercomSystem = costReviewSecurityData.costReviewSecurityEnUsIntercomSystem ?? this.costReviewSecurityEnUsIntercomSystem;
        this.costReviewSecurityEnUsCctvSystems = costReviewSecurityData.costReviewSecurityEnUsCctvSystems ?? this.costReviewSecurityEnUsCctvSystems;
        this.costReviewSecurityEnUsAccessControlSystems = costReviewSecurityData.costReviewSecurityEnUsAccessControlSystems ?? this.costReviewSecurityEnUsAccessControlSystems;
        this.costReviewSecurityEnUsNetwork = costReviewSecurityData.costReviewSecurityEnUsNetwork ?? this.costReviewSecurityEnUsNetwork;
        this.costReviewSecurityEnUsRetrofitConsiderations = costReviewSecurityData.costReviewSecurityEnUsRetrofitConsiderations ?? this.costReviewSecurityEnUsRetrofitConsiderations;
        this.costReviewSecurityEnUsIntrusionSystem = costReviewSecurityData.costReviewSecurityEnUsIntrusionSystem ?? this.costReviewSecurityEnUsIntrusionSystem;
        break;
      }
    }
  }

  createAllFormGroups() {
    if(this.business === this.constants.BUSINESS_AUTOMATION || this.business === this.constants.BUSINESS_MECHANICAL) {
      this.createExhaustFansForm();
      this.createAirHandlersForm();
      this.createHumidifiersForm();
     
    }

    if (this.business === this.constants.BUSINESS_SECURITY) {
      this.createSecurityNetworkForm();
      this.createSecurityRetrofitForm();
      this.createAccessControlSystemForm();
      this.createCctvSystemForm();
      this.createIntercomSystemForm();
      this.createIntrusionSystemForm();
      this.createExecutionScheduleForm();
    }
  }

  createIntrusionSystemForm() {
    this.intrusionSystemFormGroup = this.formBuilder.group({
      integrationWithOtherSystems: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsIntrusionSystem.integrationWithOtherSystems)),
      isNewVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.isNewVendorChecked),
      newVendor: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.newVendor),
      isReplacementVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.isReplacementVendorChecked),
      replacementVendor: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.replacementVendor),
      isExpansionVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.isExpansionVendorChecked),
      expansionVendor: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.expansionVendor),
      isMonitoringVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.isMonitoringVendorChecked),
      monitoringVendor: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.monitoringVendor),
      detailedDrawings: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsIntrusionSystem.detailedDrawings)),
      liftRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsIntrusionSystem.liftRequired)),
      risk: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.risk),
      numberOfPanels: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.numberOfPanels || 0),
      numberOfKeypads: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.numberOfKeypads || 0),
      numberOfContacts: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.numberOfContacts || 0),
      numberOfMotionDetectors: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.numberOfMotionDetectors || 0),
      localAudibleDevices: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.localAudibleDevices || 0),
      employeesToTrain: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.employeesToTrain || 0),
      lowContractor: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.lowContractor),
      numberOfBidders: this.formBuilder.control(this.costReviewSecurityEnUsIntrusionSystem.numberOfBidders || 0)
    });
    }


  createIntercomSystemForm() {
    this.intercomSystemFormGroup = this.formBuilder.group({
      integrationWithOtherSystems: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsIntercomSystem.integrationWithOtherSystems)),
      isNewVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.isNewVendorChecked),
      newVendor: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.newVendor),
      isReplacementVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.isReplacementVendorChecked),
      replacementVendor: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.replacementVendor),
      isExpansionVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.isExpansionVendorChecked),
      expansionVendor: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.expansionVendor),
      risk: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.risk),
      numberOfMasters: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.numberOfMasters || 0),
      numberOfSubstations: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.numberOfSubstations || 0),
      employeesToTrain: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.employeesToTrain || 0),
      lowContractor: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.lowContractor),
      numberOfBidders: this.formBuilder.control(this.costReviewSecurityEnUsIntercomSystem.numberOfBidders || 0)
    });
  }

  createCctvSystemForm() {
    this.cctvSystemsFormGroup = this.formBuilder.group({
      integrationWithOtherSystems: this.formBuilder.control(
      this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsCctvSystems.integrationWithOtherSystems)),
      isNewVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.isNewVendorChecked),
      newVendor: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.newVendor),
      isReplacementVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.isReplacementVendorChecked),
      replacementVendor: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.replacementVendor),
      isExpansionVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.isExpansionVendorChecked),
      expansionVendor: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.expansionVendor),
      serverProvidedBy: this.formBuilder.control(
      this.getParameterValues(this.constants.SIEMENS_CUSTOMER, this.costReviewSecurityEnUsCctvSystems.serverProvidedBy)),
      storageProvidedBy: this.formBuilder.control(
      this.getParameterValues(this.constants.SIEMENS_CUSTOMER, this.costReviewSecurityEnUsCctvSystems.storageProvidedBy)),
      clientComputer: this.formBuilder.control(
      this.getParameterValues(this.constants.SIEMENS_CUSTOMER, this.costReviewSecurityEnUsCctvSystems.clientComputer)),
      numberOfClients: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.numberOfClients || 0),
      integrationRequired: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.integrationRequired ?? []),
      liftRequired: this.formBuilder.control(
      this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsCctvSystems.liftRequired)),
      risk: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.risk),
      numberOfControllers: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.numberOfControllers || 0),
      numberOfInsideCameras: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.numberOfInsideCameras || 0),
      numberOfOutsideCameras: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.numberOfOutsideCameras || 0),
      panAndTiltCameras: this.formBuilder.control(
      this.costReviewSecurityEnUsCctvSystems.panAndTiltCameras),
      expectedStorageTime: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.expectedStorageTime || 0),
      archivingMedia: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsCctvSystems.archivingMedia)),
      employeesToTrain: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.employeesToTrain || 0),
      lowContractor: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.lowContractor),
      numberOfBidders: this.formBuilder.control(this.costReviewSecurityEnUsCctvSystems.numberOfBidders || 0)
    });
    }

  createAccessControlSystemForm() {
    this.accessControlSystemFormGroup = this.formBuilder.group({
      integrationWithOtherSystems: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsAccessControlSystems.integrationWithOtherSystems)),
      isNewVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.isNewVendorChecked),
      newVendor: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.newVendor),
      isReplacementVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.isReplacementVendorChecked),
      replacementVendor: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.replacementVendor),
      isExpansionVendorChecked: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.isExpansionVendorChecked),
      expansionVendor: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.expansionVendor),
      detailedDrawings: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsAccessControlSystems.detailedDrawings)),
      firmElectricalQuote: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsAccessControlSystems.firmElectricalQuote)),
      networkProvidedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_CUSTOMER, this.costReviewSecurityEnUsAccessControlSystems.networkProvidedBy)),
      serverProvidedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_CUSTOMER, this.costReviewSecurityEnUsAccessControlSystems.serverProvidedBy)),
      clientComputer: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_CUSTOMER, this.costReviewSecurityEnUsAccessControlSystems.clientComputer)),
      numberOfClients: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.numberOfClients || 0),
      risk: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.risk),
      numberOfPanels: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.numberOfPanels || 0),
      numberOfReaders: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.numberOfReaders || 0),
      numberOfContacts: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.numberOfContacts || 0),
      numberOfREX: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.numberOfREX || 0),
      doorHardwareType: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.doorHardwareType ?? []),
      doorHardwareProvidedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_SUBCONTRACTOR_OTHER, this.costReviewSecurityEnUsAccessControlSystems.doorHardwareProvidedBy)),
      doorHardwareInstalledBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_SUBCONTRACTOR_OTHER, this.costReviewSecurityEnUsAccessControlSystems.doorHardwareInstalledBy)),
      numberOfDoors: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.numberOfDoors || 0),
      localDoorAlarms: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.localDoorAlarms),
      employeesToTrain: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.employeesToTrain || 0),
      lowContractor: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.lowContractor),
      numberOfBidders: this.formBuilder.control(this.costReviewSecurityEnUsAccessControlSystems.numberOfBidders || 0),
      wireSpend: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsAccessControlSystems.wireSpend)),
      thirdPartyCommodity: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsAccessControlSystems.thirdPartyCommodity))
    });
  }

  createExhaustFansForm() {
    this.exhaustFansFormGroup = this.formBuilder.group({
      numberOfExhaustFans: this.formBuilder.control(this.costReviewExhaustFans.numberOfExhaustFans || 0),
      manufacturer: this.formBuilder.control(this.costReviewExhaustFans.manufacturer),
      unitsControlsFurnishedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewExhaustFans.unitsControlsFurnishedBy)),
      unitsControlsInstalledBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewExhaustFans.unitsControlsInstalledBy)),
      unitsControlsWiredBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewExhaustFans.unitsControlsWiredBy))
    });
  }

  createAirHandlersForm() {
    this.airHandlersFormGroup = this.formBuilder.group({
      numberOfAirHandlerUnits: this.formBuilder.control(this.costReviewAirHandler.numberOfAirHandlerUnits || 0),
      manufacturer: this.formBuilder.control(this.costReviewAirHandler.manufacturer),
      unitsControlsFurnishedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewAirHandler.unitsControlsFurnishedBy)),
      unitsControlsInstalledBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewAirHandler.unitsControlsInstalledBy)),
      unitsControlsWiredBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewAirHandler.unitsControlsWiredBy)),
      specialSensorsRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewAirHandler.specialSensorsRequired)),
      numberOfFans: this.formBuilder.control(this.costReviewAirHandler.numberOfFans || 0),
      description: this.formBuilder.control(this.costReviewAirHandler.description)
    });
  }

  createHumidifiersForm() {
    this.humidifiersFormGroup = this.formBuilder.group({
      numberOfHumidifires: this.formBuilder.control(this.costReviewHumidiFires.numberOfHumidifires || 0),
      manufacturer: this.formBuilder.control(this.costReviewHumidiFires.manufacturer),
      unitsControlsFurnishedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewHumidiFires.unitsControlsFurnishedBy)),
      unitsControlsInstalledBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewHumidiFires.unitsControlsInstalledBy)),
      unitsControlsWiredBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewHumidiFires.unitsControlsWiredBy))
    });
  }

  createExecutionScheduleForm() {
    this.executionScheduleFormGroup = this.formBuilder.group({
      phasedInstallation: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsExecutionSchedule.phasedInstallation)),
       describePhasingConsiderations: this.formBuilder.control(this.costReviewSecurityEnUsExecutionSchedule.describePhasingConsiderations || ''),
      projectSchedulePublished: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsExecutionSchedule.projectSchedulePublished)),
       officeTechnicalCompetencies: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsExecutionSchedule.officeTechnicalCompetencies)),
      localOfficeStaffed: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsExecutionSchedule.localOfficeStaffed)),
    specialTaggingRequirements: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsExecutionSchedule.specialTaggingRequirements)),
      specialCutoverRequirements: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsExecutionSchedule.specialCutoverRequirements))
    });
  }

  executionScheduleFormValues(formControlName: string): string {
    const control: string = this.executionScheduleFormGroup?.get(formControlName)?.value;
    return control || Constants.EMPTY_STRING;
  }

  createSecurityNetworkForm() {
    this.securityNetworkFormGroup = this.formBuilder.group({
      networkStyle: this.formBuilder.control(this.getParameterValues(this.constants.NETWORK_STYLE, this.costReviewSecurityEnUsNetwork.networkStyle)),
      networkOptions: this.formBuilder.control(this.costReviewSecurityEnUsNetwork.networkOptions ?? [])
    });
  }



  createSecurityRetrofitForm() {
    this.securityRetrofitFormGroup = this.formBuilder.group({
      reuseSystemComponents: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsRetrofitConsiderations.reuseSystemComponents)),
      existingWiringReused: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSecurityEnUsRetrofitConsiderations.existingWiringReused))
    });
  }

  getParameterValues(key: string, value: string | number | undefined) {
    value ??= Constants.EMPTY_STRING;
    return this.reviewService.getCostReviewParametersDescription(key, value);
  }

  getParameterValuesForArray(key: string, value: Array<string | number> | undefined) {
    if (!value || value.length === 0) {
      value = [Constants.EMPTY_STRING];
    }
    return this.reviewService.getCostReviewParametersDescriptionForArray(key, value);
  }

  getDropdownValues(key: string): { label: string; value: string }[] {
    return this.reviewService.getDropdownValues(key);
  }

  formControlsValueForString(formGroup: FormGroup | undefined, formControlName: string): string {
    return this.commonFunctionsService.formControlsValueForString(formGroup, formControlName);
  }

  isControlValueEqualToTranslation(formGroup: FormGroup | undefined, formControlName: string, translationKey: string): boolean {
    return this.commonFunctionsService.isControlValueEqualToTranslation(formGroup, formControlName, translationKey);
  }
  formControlsValueForNumber(formGroupName: FormGroup, formControlName: string): number {
    const controlValue: number = formGroupName?.controls[formControlName]?.value;
    return controlValue || 0;  
  }

  setFormControlValue(formGroup: FormGroup | undefined, controlName: string, value: string | number): void {
    const control = formGroup?.get(controlName);
    if (!control || control.value === value) {
      return;
    }

    control.setValue(value);
  }

  private getParameterCode(parameter: string, description: string, currentValue: number | undefined): number {
    const code = this.reviewService.getCostReviewParametersCode(parameter, description);
    return code !== undefined ? code : (currentValue ?? 0);
  }

   private getReviewLibraryState(): void {
    this.isDisabled = this.reviewDetailsTabService.getReviewLibraryDisableFlag();
  }
}
